import { Undo2, Redo2, Search, X, Tag } from "lucide-react";
import { toast } from "react-hot-toast";
import { FiRefreshCw, FiSave, FiFilter, FiInfo } from "react-icons/fi";
import GraphSelector from "../../Graph/GraphSelector";
import { GraphContext } from "../../../../../../../contexts/GraphContext";
import { BaseContext } from "../../../../../../../contexts/BaseContext";
import { useContext, useState, useEffect, useMemo, useRef } from "react";
import ProgressModal from "../../../../../ProgressTracking/ProgressModal";
import { Tooltip } from "@mui/material";
import Select from "react-select";
import { createPortal } from "react-dom";

export const ExtractBanner = ({
  // State values
  isRefreshing,

  // UI state setters
  setIsRefreshing,
  setShouldFitView,

  // New props for data slice and extract functionality
  onExtractMetadata,
  selectedNodesFileCount,
  isLoadingFileCount,
  canExtract,
  extractDisabledReason,
  onRecenterView,
  onCreateDataSlice,
}) => {
  // Context data
  const {
    deasyUserConfig,
    savedTags,
    dataSlices,
    selectedDataSlice,
    setSelectedDataSlice,
    vdbLastActiveProfileName,
  } = useContext(BaseContext);
  // Accessing these GraphContext values directly to allow Add Metadata button functionality
  const {
    handleSaveGraph,
    selectedDiscoveryGraph,
    refreshHierarchyStats,
    isRefreshingHierarchy,
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    tagNodes,
    setFocusedNodeId,
    discoveryGraphData,
    graphLayoutDirection,
    setGraphLayoutDirection,
    setHighlightedLevel,
  } = useContext(GraphContext);

  // States - move parentFilters before it's used in useMemo
  const [parentFilters, setParentFilters] = useState([]); // Track active parent filters for hierarchical filtering
  const [showInlineParentFilter, setShowInlineParentFilter] = useState(false); // Show parent filter dropdown inside main dropdown
  const [parentTagSearch, setParentTagSearch] = useState(""); // Search term for parent tags

  // Enhanced tag nodes with level information
  const enhancedTagNodes = useMemo(() => {
    if (!tagNodes) return [];

    return tagNodes.map((tag) => {
      // Calculate the depth/level of the tag (path length divided by 2 since it alternates between tags and values)
      const level = Math.floor(tag.path.length / 2) + 1;
      const parentPath = tag.path.slice(0, -1);
      const parentLabel =
        parentPath.length > 0 ? parentPath[parentPath.length - 1] : "Root";

      return {
        ...tag,
        level,
        parentLabel,
        fullPath: tag.path.join(" → "),
      };
    });
  }, [tagNodes]);

  const sortedTagNodes = useMemo(() => {
    return [...enhancedTagNodes].sort((a, b) => {
      // First sort by level, then by label
      if (a.level !== b.level) {
        return a.level - b.level;
      }
      return a.label.localeCompare(b.label);
    });
  }, [enhancedTagNodes]);

  // Get unique levels for filtering
  const uniqueLevels = useMemo(() => {
    const levels = new Set(enhancedTagNodes.map((tag) => tag.level));
    return Array.from(levels).sort((a, b) => a - b);
  }, [enhancedTagNodes]);

  // Filter tags based on parent filters
  const filteredTagNodes = useMemo(() => {
    if (parentFilters.length === 0) {
      return sortedTagNodes;
    }

    // Apply filters hierarchically
    return sortedTagNodes.filter((tag) => {
      // Check if tag matches all active filters
      return parentFilters.every((filter) => {
        // Check if this tag is a descendant of the filter
        const filterIndex = tag.path.indexOf(filter);
        return filterIndex !== -1 && filterIndex < tag.path.length - 1;
      });
    });
  }, [sortedTagNodes, parentFilters]);

  // Get unique parent tags for filter options
  const availableParentTags = useMemo(() => {
    const parents = new Set();
    filteredTagNodes.forEach((tag) => {
      // Add all parent elements from the path except the tag itself
      for (let i = 0; i < tag.path.length - 1; i++) {
        parents.add(tag.path[i]);
      }
    });
    return Array.from(parents).sort();
  }, [filteredTagNodes]);

  // States
  const [isSaving, setIsSaving] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showDataSliceDropdown, setShowDataSliceDropdown] = useState(false);
  const [showBulkOperationsDropdown, setShowBulkOperationsDropdown] =
    useState(false);
  const [showBulkImproveConfirm, setShowBulkImproveConfirm] = useState(false);
  const [tagsToImprove, setTagsToImprove] = useState([]);
  const [showBulkStandardizeModal, setShowBulkStandardizeModal] =
    useState(false);
  const [, setSearchTerm] = useState("");
  const [isChangingLayout, setIsChangingLayout] = useState(false); // Add state for layout transition
  const [expandedLevels, setExpandedLevels] = useState(new Set()); // Track which levels are expanded

  // Refs for dropdowns
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const bulkOperationsRef = useRef(null);
  const bulkOperationsButtonRef = useRef(null);

  // Get filtered data slices for current VDB profile
  const vdbProfile =
    deasyUserConfig?.vdbmConfig?.Configs[vdbLastActiveProfileName];
  const filteredDataSlices = useMemo(() => {
    let dataSourceCollection = vdbProfile?.vdb_collection_name;
    if (!dataSourceCollection) {
      dataSourceCollection = vdbProfile?.name;
    }
    return dataSlices.filter((slice) => {
      let sliceCollection = slice?.vdb_collection_name;
      if (!sliceCollection) {
        sliceCollection = slice?.vector_db_config?.name;
      }
      return sliceCollection === dataSourceCollection;
    });
  }, [dataSlices, vdbProfile]);

  useEffect(() => {
    setFocusedNodeId(null);
    setSearchTerm("");
    setSelectedTag(null);
  }, [selectedDiscoveryGraph, setFocusedNodeId, setSearchTerm]);

  useEffect(() => {
    const handleTagManuallyCleared = () => {
      setSelectedTag(null);
    };
    document.addEventListener("tagManuallyCleared", handleTagManuallyCleared);
    return () => {
      document.removeEventListener(
        "tagManuallyCleared",
        handleTagManuallyCleared,
      );
    };
  }, []);

  // Add event listener for level filtering from graph nodes
  useEffect(() => {
    const handleFilterByLevel = (event) => {
      const { level } = event.detail;
      setHighlightedLevel(level); // Highlight nodes at this level
      // Optionally clear the current tag selection when filtering by level
      setSelectedTag(null);
    };

    document.addEventListener("filterByLevel", handleFilterByLevel);
    return () => {
      document.removeEventListener("filterByLevel", handleFilterByLevel);
    };
  }, [setHighlightedLevel]);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(event.target)
      ) {
        setShowDataSliceDropdown(false);
      }

      if (
        bulkOperationsRef.current &&
        !bulkOperationsRef.current.contains(event.target) &&
        bulkOperationsButtonRef.current &&
        !bulkOperationsButtonRef.current.contains(event.target)
      ) {
        setShowBulkOperationsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close other dropdowns when one is opened
  useEffect(() => {
    if (showDataSliceDropdown) {
      setShowBulkOperationsDropdown(false);
    }
  }, [showDataSliceDropdown]);

  useEffect(() => {
    if (showBulkOperationsDropdown) {
      setShowDataSliceDropdown(false);
    }
  }, [showBulkOperationsDropdown]);

  // Add effect to handle centering after layout change
  useEffect(() => {
    if (isChangingLayout) {
      // Wait for the graph to re-render with new layout
      const timer = setTimeout(() => {
        setIsChangingLayout(false);
      }, 300); // Adjust delay as needed for smooth transition

      return () => clearTimeout(timer);
    }
  }, [isChangingLayout]);

  // Handle bulk operations
  const handleBulkOperation = (operation) => {
    setShowBulkOperationsDropdown(false);

    switch (operation) {
      case "improve":
        handleBulkImprove();
        break;
      case "standardize":
        handleBulkStandardize();
        break;
      default:
        break;
    }
  };

  // Bulk improve tags
  const handleBulkImprove = () => {
    const { deasyApiKey } = deasyUserConfig;

    if (!deasyApiKey) {
      toast.error("Authentication error: Missing API key");
      return;
    }

    // Improved tag filtering logic that recursively searches through the graph structure
    const findTagsInGraph = (data) => {
      const foundTags = new Set();

      const traverse = (obj) => {
        if (!obj || typeof obj !== "object") return;

        Object.keys(obj).forEach((key) => {
          // If the key has a TagAvailableValues property, it's a tag
          if (obj[key]?.TagAvailableValues) {
            foundTags.add(key);
          }

          // Continue traversing
          if (typeof obj[key] === "object") {
            traverse(obj[key]);
          }
        });
      };

      traverse(data);
      return foundTags;
    };

    const tagsInGraph = findTagsInGraph(discoveryGraphData);
    console.log("Tags found in graph:", [...tagsInGraph]);

    // If no tags are found in the graph structure at all
    if (tagsInGraph.size === 0) {
      toast.error(
        "No tags found in the current metadata schema. Please add some tags first.",
      );
      return;
    }

    const graphTags = savedTags.filter((tag) => tagsInGraph.has(tag.name));
    console.log("Filtered tags that exist in graph:", graphTags.length);

    if (graphTags.length === 0) {
      toast.error(
        "No tags available to improve. Your metadata schema contains tags that aren't in the saved tags database.",
      );
      return;
    }

    setTagsToImprove(graphTags);
    setShowBulkImproveConfirm(true);
  };

  // Bulk standardize tags
  const handleBulkStandardize = () => {
    const { deasyApiKey } = deasyUserConfig;

    if (!deasyApiKey) {
      toast.error("Authentication error: Missing API key");
      return;
    }

    const graphTags = savedTags.filter((tag) => {
      return Object.keys(discoveryGraphData || {}).some(
        (key) => key === tag.name,
      );
    });

    if (graphTags.length === 0) {
      toast.error("No tags available to standardize");
      return;
    }

    setTagsToImprove(graphTags);
    setShowBulkStandardizeModal(true);
  };

  // Enhanced select styles with better visual design
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#000" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px #000" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#000" : "#d1d5db",
      },
      borderRadius: "0.375rem",
      padding: "0",
      minHeight: "40px",
      backgroundColor: "white",
      transition: "all 0.15s ease",
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: "4px",
      backgroundColor: "white",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      borderRadius: "0.375rem",
      border: "1px solid #e5e7eb",
      maxHeight: "30vh",
      zIndex: 10,
      width: "50vw", // Increased width for better readability
      justifyContent: "space-between",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0.25rem",
      overflowY: "auto",
      maxHeight: "30vh",
      "::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
      },
      "::-webkit-scrollbar-track": {
        background: "#f9fafb",
        borderRadius: "3px",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#d1d5db",
        borderRadius: "3px",
        "&:hover": {
          background: "#9ca3af",
        },
      },
    }),
    option: (provided, state) => ({
      ...provided,
      padding: "0.75rem 1rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      borderRadius: "0.25rem",
      margin: "0.125rem 0",
      backgroundColor: state.isSelected
        ? "#f3f4f6"
        : state.isFocused
          ? "#f9fafb"
          : "white",
      color: state.isSelected ? "#111827" : "#374151",
      textAlign: "left",
      fontSize: "0.875rem",
      fontWeight: state.isSelected ? "500" : "400",
      transition: "all 0.1s ease",
      "&:active": {
        backgroundColor: "#e5e7eb",
      },
      ".tag-description": {
        color: "#6b7280",
      },
      ".tag-icon": {
        color: "#6b7280",
      },
    }),
    container: (provided) => ({
      ...provided,
      position: "relative",
    }),
    valueContainer: (provided) => ({
      ...provided,
      paddingLeft: "2.5rem",
      paddingRight: "0.5rem",
    }),
    input: (provided) => ({
      ...provided,
      paddingLeft: "0",
      color: "#111827",
    }),
    placeholder: (provided) => ({
      ...provided,
      paddingLeft: "0",
      color: "#9ca3af",
      fontSize: "0.875rem",
    }),
    singleValue: (provided) => ({
      ...provided,
      paddingLeft: "0",
      color: "#111827",
      fontWeight: "500",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      cursor: "pointer",
      padding: "4px",
      borderRadius: "4px",

      color: "#6b7280",
      transition: "all 0.1s ease",
      "&:hover": {
        backgroundColor: "#f3f4f6",
        color: "#111827",
      },
    }),
  };

  // Group tags by level for collapsible structure
  const groupedTagOptions = useMemo(() => {
    // Group tags by level
    const groups = {};
    filteredTagNodes.forEach((tag) => {
      const levelKey = tag.level;
      if (!groups[levelKey]) {
        groups[levelKey] = [];
      }
      groups[levelKey].push(tag);
    });

    // Convert to React Select group format with custom data
    return Object.entries(groups).map(([level, tags]) => ({
      label: `Level ${level}`,
      options: tags, // Always show all tags, let the Group component handle visibility
      data: {
        level: parseInt(level),
        count: tags.length,
        isExpanded: expandedLevels.has(parseInt(level)),
      },
    }));
  }, [filteredTagNodes, expandedLevels]);

  // Initialize with all levels expanded
  useEffect(() => {
    if (uniqueLevels.length > 0 && expandedLevels.size === 0) {
      setExpandedLevels(new Set(uniqueLevels));
    }
  }, [uniqueLevels, expandedLevels.size]);

  // Close inline parent filter when main tag selection changes
  useEffect(() => {
    setShowInlineParentFilter(false);
    setParentTagSearch("");
  }, [selectedTag]);

  return (
    <div className="flex flex-col gap-3 mb-6">
      {/* Primary Controls Row */}
      <div className="flex items-center justify-between gap-4">
        {/* Left Section - Data & View Controls */}
        <div className="flex items-center gap-4">
          {/* Data Filter */}
          <button
            ref={dropdownButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              setShowDataSliceDropdown(!showDataSliceDropdown);
            }}
            className={`
              relative flex items-center gap-2.5
              bg-white border rounded-md px-4 py-2.5 text-sm font-medium
              transition-all duration-150 ease-in-out h-11
              ${
                showDataSliceDropdown
                  ? "border-gray-900 shadow-sm"
                  : "border-gray-300 hover:border-gray-400"
              }
              ${selectedDataSlice ? "text-gray-900" : "text-gray-600"}
            `}
            aria-haspopup="listbox"
            aria-expanded={showDataSliceDropdown}
          >
            <FiFilter
              className={`w-4 h-4 ${selectedDataSlice ? "text-gray-900" : "text-gray-500"}`}
            />
            <span className="truncate">
              {selectedDataSlice ? selectedDataSlice.name : "All Data"}
            </span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${showDataSliceDropdown ? "rotate-180 text-gray-900" : "text-gray-400"}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>

            {showDataSliceDropdown && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-50 min-w-[240px] max-h-[320px] overflow-hidden"
              >
                <div className="overflow-y-auto max-h-[280px]">
                  <button
                    onClick={() => {
                      setSelectedDataSlice(null);
                      setShowDataSliceDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors
                      ${
                        !selectedDataSlice
                          ? "bg-gray-100 text-gray-900 font-medium"
                          : "hover:bg-gray-50 text-gray-700"
                      }`}
                  >
                    All Files
                  </button>
                  {filteredDataSlices.map((slice) => (
                    <button
                      key={slice.id}
                      onClick={() => {
                        setSelectedDataSlice(slice);
                        setShowDataSliceDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors
                        ${
                          selectedDataSlice?.id === slice.id
                            ? "bg-gray-100 text-gray-900 font-medium"
                            : "hover:bg-gray-50 text-gray-700"
                        }`}
                    >
                      {slice.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </button>

          {/* View Mode Toggle and Layout Toggle */}
          {createPortal(
            <div className="absolute right-5 bottom-5 flex items-center gap-3">
              {/* View Mode Toggle */}
              {/* <ViewModeToggle /> */}

              {/* Layout Toggle */}

              {/* Center View Button */}
              <Tooltip title="Center view">
                <button
                  onClick={() => setShouldFitView(true)}
                  className="p-2.5 rounded-md border-2 border-gray-400 bg-gray-50 text-gray-700 hover:bg-gray-100 hover:border-gray-500"
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M8 21H5C3.89543 21 3 20.1046 3 19V16M12 8L12 16M8 12L16 12" />
                  </svg>
                </button>
              </Tooltip>
              <Tooltip
                title={`Switch to ${graphLayoutDirection === "vertical" ? "horizontal" : "vertical"} layout`}
              >
                <button
                  onClick={() => {
                    const newDirection =
                      graphLayoutDirection === "vertical"
                        ? "horizontal"
                        : "vertical";
                    setGraphLayoutDirection(newDirection);
                    setIsChangingLayout(true); // Trigger the delayed centering
                    setShouldFitView(true);
                  }}
                  className={`flex items-center gap-2.5 bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm font-medium
                  transition-all duration-150 hover:border-gray-400 hover:shadow-sm text-gray-700 h-11
                  ${isChangingLayout ? "opacity-75 cursor-wait" : ""}`}
                  disabled={isChangingLayout}
                >
                  {graphLayoutDirection === "vertical" ? (
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 3v18M3 12h18" />
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 12h18M12 3v18" />
                    </svg>
                  )}
                  <span className="capitalize">
                    {isChangingLayout ? "Changing..." : graphLayoutDirection}
                  </span>
                </button>
              </Tooltip>
            </div>,
            document.body,
          )}
        </div>
        <div className="h-6 w-px bg-gray-300" />

        {/* Center Section - Search & Schema */}
        {/* Combined Tag Search Container */}
        <div className="bg-white border border-gray-300 rounded-md pl-2 flex items-center gap-2 w-[45%] min-w-[600px]">
          {/* Parent Filters */}
          {parentFilters && parentFilters.length > 0 && (
            <div className="flex items-center gap-1 px-2 border-r border-gray-300">
              {parentFilters.map((filter, index) => (
                <div
                  key={filter}
                  className="flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
                >
                  <span>{filter}</span>
                  <button
                    onClick={() => {
                      setParentFilters((prev) =>
                        prev.filter((_, i) => i !== index),
                      );
                    }}
                    className="hover:bg-gray-200 rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setParentFilters([])}
                className="text-xs text-gray-500 hover:text-gray-700 px-1"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Tag Search */}
          <div className="relative flex-1">
            <Select
              options={groupedTagOptions}
              className="text-left"
              classNamePrefix="tag-select"
              placeholder={`Search ${filteredTagNodes.length} tags by name or parent...`}
              isClearable
              isSearchable
              value={selectedTag}
              filterOption={(option, searchText) => {
                if (!searchText) return true;

                const search = searchText.toLowerCase();
                const label = option.label.toLowerCase();
                const fullPath = option.data?.fullPath?.toLowerCase() || "";
                const parentLabel =
                  option.data?.parentLabel?.toLowerCase() || "";
                const description =
                  option.data?.description?.toLowerCase() || "";

                // Search in tag name, full path, parent label, or description
                return (
                  label.includes(search) ||
                  fullPath.includes(search) ||
                  parentLabel.includes(search) ||
                  description.includes(search)
                );
              }}
              onChange={(selectedOption) => {
                setSelectedTag(selectedOption);
                if (selectedOption) {
                  console.log("Tag selected for navigation:", {
                    value: selectedOption.value,
                    label: selectedOption.label,
                    path: selectedOption.path,
                  });
                  setFocusedNodeId(selectedOption.value);
                  setSearchTerm(selectedOption.label);
                } else {
                  setFocusedNodeId(null);
                  setSearchTerm("");
                  document.dispatchEvent(
                    new CustomEvent("tagExplicitlyDeselected"),
                  );
                }
              }}
              styles={{
                ...selectStyles,
                control: (provided, state) => ({
                  ...selectStyles.control(provided, state),
                  border: "none",
                  boxShadow: "none",
                  minHeight: "32px",
                  minWidth: "400px",
                  "&:hover": {
                    border: "none",
                  },
                }),
                menu: (provided) => ({
                  ...provided,
                  minWidth: "500px",
                  maxHeight: "500px",
                }),
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: "400px",
                }),
              }}
              formatOptionLabel={(option, { context }) => {
                if (context === "value") {
                  return (
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <Tag size={12} className="text-gray-500" />
                        <span className="text-sm">{option.label}</span>
                        {option.parentLabel &&
                          option.parentLabel !== "Root" && (
                            <span className="text-xs text-gray-500">
                              in {option.parentLabel}
                            </span>
                          )}
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Tag size={12} className="text-gray-600 flex-shrink-0" />
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="flex items-center justify-between w-full">
                          <span className="font-medium truncate text-gray-900">
                            {option.label}
                          </span>
                        </div>
                        <div className="flex items-center justify-between w-full mt-1">
                          {option.description && (
                            <div
                              className="text-xs text-gray-500 truncate flex-1"
                              title={option.description}
                            >
                              {option.description}
                            </div>
                          )}
                          {option.parentLabel &&
                            option.parentLabel !== "Root" && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded flex-shrink-0 ml-2">
                                {option.parentLabel}
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
                MenuList: ({ children, ...props }) => {
                  const showExpandControls = uniqueLevels.length > 1;

                  // Filter parent tags based on search
                  const filteredParentTags = availableParentTags.filter(
                    (parent) =>
                      !parentFilters.includes(parent) &&
                      parent
                        .toLowerCase()
                        .includes(parentTagSearch.toLowerCase()),
                  );

                  return (
                    <div
                      {...props}
                      style={{
                        ...props.style,
                        maxHeight: "500px",
                        overflowY: "auto",
                      }}
                    >
                      {/* Parent Filter Section */}
                      <div className="border-b border-gray-200 bg-gray-50/50">
                        {/* Active Filters */}
                        {parentFilters.length > 0 && (
                          <div className="px-4 pt-3 pb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-600 font-medium">
                                Active filters:
                              </span>
                              {parentFilters.map((filter, index) => (
                                <div
                                  key={filter}
                                  className="flex items-center gap-1 bg-white border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-md shadow-sm"
                                >
                                  <span>{filter}</span>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setParentFilters((prev) =>
                                        prev.filter((_, i) => i !== index),
                                      );
                                    }}
                                    className="hover:bg-gray-100 rounded-full p-0.5 transition-colors"
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setParentFilters([]);
                                }}
                                className="text-xs text-gray-500 hover:text-gray-700 px-1 underline"
                              >
                                Clear all
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Search and Filter Section */}
                        <div className="p-4 space-y-3">
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-700 font-medium flex-shrink-0">
                              Filter by parent:
                            </span>
                            <div className="relative flex-1">
                              <input
                                type="text"
                                placeholder="Search parent tags..."
                                value={parentTagSearch}
                                onChange={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setParentTagSearch(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                }}
                                className="w-full bg-white border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                              />
                              <Search
                                size={14}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                              />
                            </div>
                          </div>

                          {/* Parent Tags List */}
                          {filteredParentTags.length > 0 &&
                            (parentTagSearch || !showInlineParentFilter) && (
                              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                                {filteredParentTags.map((parent) => (
                                  <button
                                    key={parent}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setParentFilters((prev) => [
                                        ...prev,
                                        parent,
                                      ]);
                                      setParentTagSearch("");
                                    }}
                                    className="bg-white border border-gray-200 text-gray-700 text-xs px-2.5 py-1 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors flex-shrink-0"
                                  >
                                    {parent}
                                  </button>
                                ))}
                              </div>
                            )}

                          {filteredParentTags.length === 0 &&
                            parentTagSearch && (
                              <div className="text-sm text-gray-500 text-center py-2">
                                No parent tags found matching "{parentTagSearch}
                                "
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Expand/Collapse Controls */}
                      {showExpandControls && (
                        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-white">
                          <span className="text-xs text-gray-600">Levels:</span>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setExpandedLevels(new Set(uniqueLevels));
                            }}
                            className="text-xs text-gray-600 hover:text-gray-900 cursor-pointer underline"
                          >
                            Expand all
                          </button>
                          <span className="text-gray-300">•</span>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setExpandedLevels(new Set());
                            }}
                            className="text-xs text-gray-600 hover:text-gray-900 cursor-pointer underline"
                          >
                            Collapse all
                          </button>
                        </div>
                      )}
                      {children}
                    </div>
                  );
                },
                Group: ({ children, label, data, selectProps, ...props }) => {
                  const isExpanded = expandedLevels.has(data.data.level);

                  const handleToggle = (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setExpandedLevels((prev) => {
                      const newSet = new Set(prev);
                      if (newSet.has(data.data.level)) {
                        newSet.delete(data.data.level);
                      } else {
                        newSet.add(data.data.level);
                      }
                      return newSet;
                    });
                  };

                  return (
                    <div>
                      <div
                        onClick={handleToggle}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <svg
                            className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                          <span className="text-sm font-medium text-gray-700">
                            {label}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({data.data.count})
                          </span>
                        </div>
                      </div>
                      {isExpanded && <div>{children}</div>}
                    </div>
                  );
                },
              }}
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
              <Search size={16} />
            </div>
          </div>
          <Tooltip
            title="You can search tags by name or filter by parent tag. For example, typing a parent tag name will show all its child tags."
            arrow
            placement="bottom"
          >
            <div className="pr-2 text-gray-400 hover:text-gray-600 cursor-help">
              <FiInfo size={16} />
            </div>
          </Tooltip>
        </div>

        <div className="h-6 w-px bg-gray-300" />
        {/* Schema Selector */}
        <div className="min-w-[200px]">
          <GraphSelector />
        </div>
        <div className="h-6 w-px bg-gray-300" />

        <div className=" flex items-center justify-center">
          {/* Right Section - Actions */}
          <div className="flex items-center gap-2">
            {/* Quick Actions */}
            <Tooltip title="Undo">
              <button
                onClick={handleUndo}
                className={`p-2.5 rounded-md border ${
                  !canUndo
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-600 hover:text-gray-900 border-gray-300 hover:border-gray-400"
                }`}
                disabled={!canUndo}
              >
                <Undo2 size={16} />
              </button>
            </Tooltip>

            <Tooltip title="Redo">
              <button
                onClick={handleRedo}
                className={`p-2.5 rounded-md border ${
                  !canRedo
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-600 hover:text-gray-900 border-gray-300 hover:border-gray-400"
                }`}
                disabled={!canRedo}
              >
                <Redo2 size={16} />
              </button>
            </Tooltip>

            <Tooltip title="Save">
              <button
                onClick={async () => {
                  setIsSaving(true);
                  await handleSaveGraph();
                  setIsSaving(false);
                  toast.success(`Schema saved successfully`);
                }}
                className="p-2.5 rounded-md border bg-white text-gray-600 hover:text-gray-900 border-gray-300 hover:border-gray-400"
                disabled={isSaving}
              >
                <FiSave size={16} />
              </button>
            </Tooltip>

            <Tooltip title="Refresh">
              <button
                onClick={async () => {
                  setIsRefreshing(true);
                  await refreshHierarchyStats(deasyUserConfig.deasyApiKey);
                  setIsRefreshing(false);
                }}
                className="p-2.5 rounded-md border bg-white text-gray-600 hover:text-gray-900 border-gray-300 hover:border-gray-400"
                disabled={isRefreshing || isRefreshingHierarchy}
              >
                <FiRefreshCw
                  size={16}
                  className={
                    isRefreshing || isRefreshingHierarchy ? "animate-spin" : ""
                  }
                />
              </button>
            </Tooltip>
          </div>
        </div>
        <div className="w-px h-8 bg-gray-300" />

        <div className="flex items-center justify-center">
          {/* Manage Tags Button */}
          <button
            ref={bulkOperationsButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              setShowBulkOperationsDropdown(!showBulkOperationsDropdown);
            }}
            className={`
              relative flex items-center gap-2.5
              bg-primary text-white rounded-md px-4 py-2.5 text-sm font-medium
              transition-all duration-150 h-11
              ${
                showBulkOperationsDropdown
                  ? "bg-primary"
                  : "hover:bg-primary/40"
              }
            `}
          >
            <span>Bulk Actions</span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${showBulkOperationsDropdown ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>

            {showBulkOperationsDropdown && (
              <div
                ref={bulkOperationsRef}
                className="absolute top-full right-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-50 overflow-hidden min-w-[240px]"
              >
                <button
                  onClick={() => {
                    if (onExtractMetadata) {
                      onExtractMetadata();
                    }
                    setShowBulkOperationsDropdown(false);
                  }}
                  disabled={!canExtract}
                  className={`w-full text-left px-4 py-3 text-sm flex items-center justify-between gap-3 transition-colors duration-150 ${
                    !canExtract
                      ? "text-gray-400 cursor-not-allowed bg-gray-50"
                      : "hover:bg-gray-50 text-gray-700"
                  }`}
                  title={extractDisabledReason || ""}
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-4 h-4 text-gray-500 flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 12h6m-3-3v6m-7 0h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2z" />
                    </svg>
                    <span>Extract & Classify</span>
                  </div>
                  {selectedNodesFileCount > 0 && canExtract && (
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0">
                      {selectedNodesFileCount.toLocaleString()}
                    </span>
                  )}
                </button>

                <div className="border-t border-gray-200">
                  <button
                    onClick={() => handleBulkOperation("improve")}
                    className="w-full text-left px-4 py-3 text-sm flex items-center justify-between gap-3 hover:bg-gray-50 text-gray-700 transition-colors duration-150"
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-4 h-4 text-gray-500 flex-shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" />
                      </svg>
                      <span>Improve Descriptions</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Secondary Info Row - Simplified */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <div className="flex items-center gap-4">
          {selectedDataSlice && <span>Filtered: {selectedDataSlice.name}</span>}
          <span>
            {filteredTagNodes.length} total tags
            {uniqueLevels.length > 0 && ` • ${uniqueLevels.length} levels`}
          </span>
        </div>

        {/* Level distribution summary */}
        {uniqueLevels.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs">Distribution:</span>
            {uniqueLevels.map((level) => {
              const count = filteredTagNodes.filter(
                (tag) => tag.level === level,
              ).length;
              return (
                <span key={level} className="text-xs">
                  L{level}: {count}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <ProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
      />

      {/* Bulk Improve Tags Confirmation Modal */}
      {showBulkImproveConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-md shadow-xl overflow-hidden w-full max-w-lg transform transition-all mx-4">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <span className="text-lg font-medium text-gray-900">
                Confirm Tag Enhancement
              </span>
              <button
                onClick={() => setShowBulkImproveConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                This will enhance the descriptions of{" "}
                <span className="font-semibold">{tagsToImprove.length}</span>{" "}
                tags in this graph.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => setShowBulkImproveConfirm(false)}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowBulkImproveConfirm(false);
                  document.dispatchEvent(
                    new CustomEvent("bulkImproveConfirmed", {
                      detail: { tags: tagsToImprove },
                    }),
                  );
                }}
                className="px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Standardize Modal - trigger event for parent component to handle */}
      {showBulkStandardizeModal &&
        (document.dispatchEvent(
          new CustomEvent("bulkStandardizeRequested", {
            detail: { tags: tagsToImprove },
          }),
        ),
        setShowBulkStandardizeModal(false))}
    </div>
  );
};
